import { supabase } from "@/integrations/supabase/client";
import type { TranslationKey } from "@/types/i18n.types";

export interface SendQuoteParams {
  draftId: string;
  pdfBase64?: string;
  pdfFilename?: string;
  to: string;
  subject: string;
  shouldSendEmail: boolean;
  sharingMethod: 'create_user' | 'existing_user' | 'shared_link';
  linkPassword?: string;
  sharedLinkUrl?: string | null;
  variables: {
    client_first_name: string;
    client_last_name: string;
    itinerary_title: string;
    start_date: string;
    end_date: string;
    custom_message?: string;
    shared_link_url?: string | null;
    payment: string;
    logo_url?: string;
    cancellation_policy_notes?: string;
  };
}

export interface SendQuoteResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
  message?: string;
}

/**
 * Centralizes the send-quote edge function invocation.
 * This should be the ONLY place where send-quote is called from the frontend.
 */
export const sendQuote = async (params: SendQuoteParams): Promise<SendQuoteResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke("send-quote", {
      body: JSON.stringify({
        draftId: params.draftId,
        pdfBase64: params.pdfBase64,
        pdfFilename: params.pdfFilename,
        to: params.to,
        subject: params.subject,
        // Backend logic parameters
        shouldSendEmail: params.shouldSendEmail,
        sharingMethod: params.sharingMethod,
        linkPassword: params.linkPassword,
        sharedLinkUrl: params.sharedLinkUrl,
        // Mailgun template variables only
        variables: params.variables,
      }),
    });

    if (error) {
      const responseBody = (data as any) || {};
      return {
        success: false,
        error: responseBody?.error || 'send_quote_failed',
        errorCode: responseBody?.error,
        message: responseBody?.message,
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending quote:', error);
    return {
      success: false,
      error: 'send_quote_exception',
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Ritira un preventivo gia' inviato riportandolo in bozza, cosi' il viaggiatore non lo vede
 * piu' mentre il TA lo corregge e lo rimanda.
 *
 * Non e' un semplice cambio di stato: la RPC rifiuta se il viaggiatore ha gia' pagato o ha
 * un pagamento in corso. Per questo non passa da updateItineraryStatus, che scrive
 * direttamente sulla tabella e non saprebbe fare quel controllo senza una race.
 */
export const revokeItineraryQuote = async (itineraryId: string): Promise<void> => {
  const { error } = await supabase.rpc('revoke_itinerary_quote' as any, {
    p_itinerary_id: itineraryId,
  });

  if (error) throw error;
};

/** Errori della revoca, tradotti in chiavi i18n. Il messaggio del database non si mostra mai. */
export const revokeQuoteErrorKey = (error: unknown): TranslationKey => {
  const message = (error as { message?: string })?.message ?? '';
  const known = ['forbidden', 'itinerary_has_payments', 'itinerary_not_sent', 'itinerary_not_found'];
  const match = known.find((key) => message.includes(key));
  return match
    ? (`builder.pricingLock.errors.${match}` as TranslationKey)
    : 'builder.pricingLock.errors.generic';
};

/**
 * Update itinerary status
 */
export const updateItineraryStatus = async (
  itineraryId: string,
  status: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('itineraries')
      .update({ status })
      .eq('id', itineraryId);

    if (error) {
      console.error('Error updating itinerary status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating itinerary status:', error);
    return false;
  }
};
